import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface CN54ReportParams {
  serviceTypes: string[];
  mailFormats: string[];
  formatType: 'html' | 'pdf' | 'xls';
  fromQuarter?: number;
  toQuarter?: number;
  fromYear?: number;
  toYear?: number;
  table?: string;
  designatedOperator?: string;
}

interface StratumRow {
  originOffice: string;
  destinationOffice: string;
  receptacleCategory: string;
  sampledGrossWeight: number;
  sampledTareWeight: number;
  totalGrossWeight: number;
  psSampledNofItems: number;
  psSampledNetWeight: number;
  stratum: string;
  reportItem: any;
  gsampledNetWeight: number;
  esampledNofItems: number;
  esampledNetWeight: number;
  psCalculatedSampledGrossWeight: number;
  gcalculatedSampledGrossWeight: number;
  gsampledNofItems: number;
  ecalculatedSampledGrossWeight: number;
  psEstimatedGrossWeight: number;
  psEstimatedNofItems: number;
  gestimatedGrossWeight: number;
  gestimatedNofItems: number;
  eestimatedGrossWeight: number;
  eestimatedNofItems: number;
}

interface ItemKey {
  fileName: string;
  mailCategory: string;
  mailFormat: string;
  sectionType: string;
}

export interface MailItem {
  key: ItemKey;
  operator: string;
  stratumRows: StratumRow[];
  formatMixed: boolean;
  formatSeparated: boolean;
  subTotalRow?: {
    psEstimatedNofItemsSubTotal?: number;
    gestimatedGrossWeightSubTotal?: number;
    gestimatedNofItemsSubTotal?: number;
    eestimatedGrossWeightSubTotal?: number;
    eestimatedNofItemsSubTotal?: number;
    totalGrossWeightSubTotal?: number;
    psEstimatedGrossWeightSubTotal?: number;
  };
  weightProportionsAndIpkRow?: {
    gipk?: number;
    eipk?: number;
    psIpk?: number;
    ipk?: number;
    psWeightProportion?: number;
    gweightProportion?: number;
    eweightProportion?: number;
  };
  sampledGrossWeightScale?: number;
}

export interface OperatorData {
  operator: string;
  items: MailItem[];
}

export type MailDataStructure = OperatorData[];

export interface CN54ReportResponse {
  // For HTML format, contains the table data
  data?: MailDataStructure;
  url?: string;
  content?: Blob;
  filename?: string;
}

export function useCN54Report() {
  return useMutation<CN54ReportResponse, Error, CN54ReportParams>({
    mutationFn: async (params: CN54ReportParams) => {
      // Create URLSearchParams to handle array parameters without brackets
      const searchParams = new URLSearchParams();
      
      // Add service types
      params.serviceTypes.forEach(type => {
        searchParams.append('serviceTypes', type);
      });
      
      // Add mail formats
      params.mailFormats.forEach(format => {
        searchParams.append('mailFormats', format);
      });
      

      
      // Add format type
      searchParams.append('formatType', params.formatType);
      
      // Add optional parameters
      if (params.fromQuarter) {
        searchParams.append('fromQuarter', params.fromQuarter.toString());
      }
      if (params.toQuarter) {
        searchParams.append('toQuarter', params.toQuarter.toString());
      }
      if (params.fromYear) {
        searchParams.append('fromYear', params.fromYear.toString());
      }
      if (params.toYear) {
        searchParams.append('toYear', params.toYear.toString());
      }
      if (params.table) {
        searchParams.append('table', params.table);
      }
      if (params.designatedOperator) {
        searchParams.append('designatedOperator', params.designatedOperator);
      }

      const response = await axios.get(`/reports/cn54?${searchParams.toString()}`, {
        // HTML returns JSON with table data, PDF/XLS return binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
      });

      // For HTML format, return the table data
      if (params.formatType === 'html') {
        return { data: response.data };
      }

      // For PDF/XLS, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `CN54_Report_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = params.formatType === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        content: response.data,
        filename: fileName
      };
    },
  });
} 